/* eslint-disable prettier/prettier */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// Define field error type
export interface ApiFieldError {
  field?: string;
  message: string;
  [key: string]: any;
}

// Define API response type
export interface ApiResponse<T = unknown> {
  data: T | null;
  error?: string;
  errors?: ApiFieldError[] | Record<string, string> | any;
  status?: number;
  rawResponse?: any;
}

// Create axios instance with base URL
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token if available
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const userEmail = localStorage.getItem('userEmail');
    if (userEmail) {
      config.headers['x-email'] = userEmail;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  response => response,
  (error: AxiosError) => {
    if (error.response) {
      if (error.response.status === 401) {
        // Clear auth tokens from localStorage
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        localStorage.removeItem('selectedRole');
        localStorage.removeItem('SocialLoginToken');
        sessionStorage.removeItem('otp_resend_cooldown_end');

        // Redirect to login page
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Generic HTTP GET request
 * @param url - The endpoint URL
 * @param config - Optional axios request config
 * @returns Promise with API response
 */
export async function get<T = unknown>(
  url: string,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  try {
    const response: AxiosResponse<T> = await api.get<T>(url, config);
    return {
      data: response.data,
      status: response.status,
    };
  } catch (error: unknown) {
    return handleError<T>(error);
  }
}

/**
 * Generic HTTP POST request
 * @param url - The endpoint URL
 * @param data - The request payload
 * @param config - Optional axios request config
 * @returns Promise with API response
 */
export async function post<T = unknown, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  try {
    const response: AxiosResponse<T> = await api.post<T>(url, data, config);
    return {
      data: response.data,
      status: response.status,
    };
  } catch (error: unknown) {
    return handleError<T>(error);
  }
}

/**
 * Generic HTTP PUT request
 * @param url - The endpoint URL
 * @param data - The request payload
 * @param config - Optional axios request config
 * @returns Promise with API response
 */
export async function put<T = unknown, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  try {
    const response: AxiosResponse<T> = await api.put<T>(url, data, config);
    return {
      data: response.data,
      status: response.status,
    };
  } catch (error: unknown) {
    return handleError<T>(error);
  }
}

/**
 * Generic HTTP DELETE request
 * @param url - The endpoint URL
 * @param config - Optional axios request config
 * @returns Promise with API response
 */
export async function del<T = unknown>(
  url: string,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  try {
    const response: AxiosResponse<T> = await api.delete<T>(url, config);
    return {
      data: response.data,
      status: response.status,
    };
  } catch (error: unknown) {
    return handleError<T>(error);
  }
}

/**
 * Generic HTTP PATCH request
 * @param url - The endpoint URL
 * @param data - The request payload
 * @param config - Optional axios request config
 * @returns Promise with API response
 */
export async function patch<T = unknown, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  try {
    const response: AxiosResponse<T> = await api.patch<T>(url, data, config);
    return {
      data: response.data,
      status: response.status,
    };
  } catch (error: unknown) {
    return handleError<T>(error);
  }
}

/**
 * Handle API errors consistently
 * @param error - The error object from axios
 * @returns Formatted error response
 */
function handleError<T>(error: unknown): ApiResponse<T> {
  if (axios.isAxiosError(error)) {
    const resData = error.response?.data as any;
    let errorMsg = resData?.message;
    if (!errorMsg && typeof resData?.error === 'string') {
      errorMsg = resData.error;
    }
    if (!errorMsg && Array.isArray(resData?.errors) && resData.errors.length > 0) {
      errorMsg = resData.errors.map((e: any) => e.message || `${e.field}: invalid`).join(', ');
    }
    if (!errorMsg) {
      errorMsg = error.message;
    }

    return {
      data: null,
      error: errorMsg,
      errors: resData?.errors || resData?.fieldErrors || (Array.isArray(resData) ? resData : undefined),
      status: error.response?.status,
      rawResponse: resData,
    };
  }

  return {
    data: null,
    error: error instanceof Error ? error.message : 'An unexpected error occurred',
  };
}

// Export the axios instance in case direct access is needed
export { api };
